import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://lutrolls.com';

export interface SEOConfig {
  path?: string;
  title?: string;
  description?: string;
}

export interface CanonicalLinkProps {
  href: string;
  title?: string;
  description?: string;
}

const updateMetaTag = (name: string, content: string) => {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const updateCanonicalLink = (href: string) => {
  // Remove existing canonical link
  const existing = document.querySelector('link[rel="canonical"]');
  existing?.remove();
  
  // Create new canonical link
  const link = document.createElement('link');
  link.rel = 'canonical';
  link.href = href;
  document.head.appendChild(link);
  
  // Also update Open Graph URL if it exists
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) {
    ogUrl.setAttribute('content', href);
  } else {
    // Create og:url if it doesn't exist
    const ogUrlMeta = document.createElement('meta');
    ogUrlMeta.setAttribute('property', 'og:url');
    ogUrlMeta.setAttribute('content', href);
    document.head.appendChild(ogUrlMeta);
  }
  
  return link;
};

export function useCanonicalUrl(config?: SEOConfig | string, title?: string, description?: string) {
  const location = useLocation();
  
  useEffect(() => {
    let seoConfig: SEOConfig;
    
    if (typeof config === 'string') {
      seoConfig = { path: config, title, description };
    } else {
      seoConfig = config || {};
    }
    
    const path = seoConfig.path || location.pathname;
    const canonicalUrl = `${SITE_URL}${path}`;
    
    const canonicalLink = updateCanonicalLink(canonicalUrl);
    
    if (seoConfig.title) {
      document.title = seoConfig.title;
    }
    
    if (seoConfig.description) {
      updateMetaTag('description', seoConfig.description);
    }
    
    return () => canonicalLink.remove();
  }, [config, title, description, location.pathname]);
  
  return {
    canonicalUrl: `${SITE_URL}${typeof config === 'string' ? config : (config?.path || location.pathname)}`,
    siteUrl: SITE_URL
  };
}

